


























































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import PerformanceItem from '@/components/molecules/PerformanceItem/PerformanceItem.vue';

import { IChartSerie } from '@/models/performance';

import BusinessService from '@/api/BusinessService';

import { goToElement } from '@/utils/goToElement';
import segmentIo from '@/utils/segment.io';

import PeriodTabs from '@/componentsV2/molecules/PeriodTabs/PeriodTabs.vue';
import Typography from '@/componentsV2/atoms/Typography/Typography.vue';
import BaseChart from '@/componentsV2/molecules/Chart/BaseChart.vue';
import PerformanceUtils, { EPeriodPerformance } from '@/utils/PerformanceUtils';
import AnalyticsCard, {
  AnalyticsCardProps,
  EAnalyticsCardArrowDirection
} from '@/componentsV2/molecules/AnalyticsCard/AnalyticsCard.vue';
import { EBusinessReportType } from '@/models/business';
import { IPeriod } from '@/models/period';

interface TCardProps extends AnalyticsCardProps {
  hideGrowth?: boolean;
  hideMetric?: boolean;
  trackEvent?: boolean;
  keyData?: string;
  loaded?: boolean;
}

interface IGraphProps extends AnalyticsCardProps {
  loaded?: boolean;
  label?: string;
  slug?: string;
  data: number[];
}

export enum EPerformanceGraphType {
  NEW_MEMBERS = 'new_members',
  OFFER_REDEMPTIONS = 'offer_redemptions',
  TOTAL_OFFERS_SAVED = 'total_offers_saved',
  BUSINESS_IMPRESSIONS = 'business_impressions',
  NON_MEMBERS_REACHED = 'non_members_reached',
  COINS_MINTED = 'coins_minted',
  TOTAL_COINS_REDEEMED = 'total_coins_redeemed',
  MEMBERS_REDEEMING_COINS = 'members_redeeming_coins'
}

export const defaultPerformanceGraphs: Record<string, IGraphProps> = {
  newMembers: {
    loaded: false,
    label: 'New Members',
    data: [],
    slug: EPerformanceGraphType.NEW_MEMBERS,
    reportType: [EBusinessReportType.OFFER_COINS]
  },
  offerRedemptions: {
    loaded: false,
    label: 'Offer Redemptions',
    data: [],
    slug: EPerformanceGraphType.OFFER_REDEMPTIONS,
    reportType: [EBusinessReportType.OFFER, EBusinessReportType.OFFER_COINS]
  },
  totalOffersSaved: {
    loaded: false,
    label: 'Total Offers Saved',
    data: [],
    slug: EPerformanceGraphType.TOTAL_OFFERS_SAVED,
    reportType: [EBusinessReportType.OFFER]
  },
  businessImpressions: {
    loaded: false,
    label: 'Business Impressions',
    data: [],
    slug: EPerformanceGraphType.BUSINESS_IMPRESSIONS,
    reportType: [EBusinessReportType.OFFER, EBusinessReportType.OFFER_COINS]
  },
  nonMembersReached: {
    loaded: false,
    data: [],
    label: 'Non-Members Reached',
    slug: EPerformanceGraphType.NON_MEMBERS_REACHED,
    reportType: [EBusinessReportType.OFFER]
  },
  coinsMinted: {
    loaded: false,
    data: [],
    label: 'Coins Minted',
    slug: EPerformanceGraphType.COINS_MINTED,
    reportType: [EBusinessReportType.COIN]
  },
  totalCoinsRedeemed: {
    loaded: false,
    data: [],
    label: 'Total Coins Redeemed',
    slug: EPerformanceGraphType.TOTAL_COINS_REDEEMED,
    reportType: [EBusinessReportType.COIN]
  },
  membersRedeemingCoins: {
    loaded: false,
    data: [],
    label: 'Members Redeeming Coins',
    slug: EPerformanceGraphType.MEMBERS_REDEEMING_COINS,
    reportType: [EBusinessReportType.COIN]
  }
};

export const defaultPerformanceHighlights: Record<string, TCardProps> = {
  loyaltyMembers: {
    amount: 0,
    loaded: false,
    link: '/performance/overview',
    reportType: [EBusinessReportType.OFFER_COINS],
    keyData: 'loyaltyMembers'
  },
  redemptions: {
    amount: 0,
    loaded: false,
    link: '/performance/overview',
    reportType: [EBusinessReportType.OFFER, EBusinessReportType.OFFER_COINS],
    keyData: 'offerPerformanceOverview'
  },
  savedOffers: {
    amount: 0,
    loaded: false,
    link: '/performance/activities',
    reportType: [EBusinessReportType.OFFER],
    keyData: 'offerPerformanceOverview'
  },
  nonMemberReached: {
    amount: 0,
    loaded: false,
    link: '/performance/overview',
    reportType: [EBusinessReportType.OFFER],
    keyData: 'impressionPerformanceOverview'
  },
  coinsInCirculation: {
    amount: 0,
    loaded: false,
    link: '/performance/overview',
    reportType: [EBusinessReportType.COIN],
    counterFractionDigits: 2,
    hideGrowth: true,
    keyData: 'coinsInCirculation'
  },
  coinsMinted: {
    amount: 0,
    loaded: false,
    link: '/performance/overview',
    reportType: [EBusinessReportType.COIN],
    counterFractionDigits: 2,
    keyData: 'coinsMinted'
  },
  redeemedCoins: {
    amount: 0,
    loaded: false,
    link: '/performance/overview',
    reportType: [EBusinessReportType.COIN, EBusinessReportType.OFFER_COINS],
    counterFractionDigits: 2,
    keyData: 'redeemedCoinsOverview'
  },
  paymentCodeConversionRatio: {
    amount: 0,
    loaded: false,
    sufixAmount: '%',
    link: '/performance/overview',
    reportType: [EBusinessReportType.COIN],
    keyData: 'paymentRelatedToRedemptionsOverview'
  },
  membersRedeemedCoins: {
    amount: 0,
    loaded: false,
    link: '/performance/overview',
    reportType: [EBusinessReportType.COIN],
    keyData: 'redeemedCoinsOverview'
  },
  paymentRelatedToRedemptions: {
    amount: 0,
    loaded: false,
    prefixAmount: '',
    link: '/performance/overview',
    counterFractionDigits: 2,
    reportType: [EBusinessReportType.COIN],
    keyData: 'paymentRelatedToRedemptionsOverview'
  },
  businessImpressions: {
    amount: 0,
    loaded: false,
    link: '/performance/overview',
    reportType: [EBusinessReportType.OFFER, EBusinessReportType.OFFER_COINS],
    keyData: 'impressionPerformanceOverview'
  },
  totalFavorites: {
    amount: 0,
    loaded: false,
    link: '/performance/customer-center',
    linkClickTrackEvent: 'Home | Clicked Total Favorites Metric See More',
    reportType: [EBusinessReportType.OFFER, EBusinessReportType.OFFER_COINS],
    hideMetric: true,
    trackEvent: true,
    keyData: 'favoritesPerformanceOverview'
  }
};

@Component({
  components: {
    PerformanceItem,
    PeriodTabs,
    Typography,
    BaseChart,
    AnalyticsCard
  }
})
export default class PerformanceHighlights extends Vue {
  @Prop() overviewData?: any;
  @Prop() performanceData?: any;
  @Prop() periodActive!: EPeriodPerformance;
  @Prop() period!: IPeriod;
  @Prop() loading!: boolean;
  @Prop() series!: IChartSerie[];
  @Prop() hasFavoritesMetric!: boolean;

  loaded = false;
  businessService = new BusinessService();
  totalValues: Record<string, TCardProps> = defaultPerformanceHighlights;
  options = PerformanceUtils.chartOptions({
    colors: ['#3E24D6']
  });

  observer: IntersectionObserver | null = null;
  triggerIntersected = false;
  eventsAlreadySent = false;

  get isMobileDevice() {
    return this.$mobileCheck();
  }

  get tPrefix() {
    return 'home.performance';
  }

  get customerInactivityDaysConfig() {
    return this.business.inactiveCustomerDays;
  }

  get reportType() {
    return this.business.reportType;
  }

  get transformedTotalValues() {
    const result = Object.entries(this.totalValues)
      .map(([key, value]) => ({
        ...value,
        ...(value.hasOwnProperty('prefixAmount') && {
          prefixAmount: this.currencySymbol
        }),
        largeTitle: String(this.$t(`${this.tPrefix}.cards.${key}.label`)),
        tooltipText: String(
          this.$t(`${this.tPrefix}.cards.${key}.tooltipText`)
        ),
        linkLabel: 'See more',
        slug: key
      }))
      .filter(
        item =>
          !item.hideMetric &&
          (item.reportType
            ? item.reportType.includes(
                this.overviewData.reportType || this.business.reportType
              )
            : true)
      );

    return result;
  }

  get currencySymbol() {
    return this.$store.getters['internationalization/getCountrySettings']
      .currencySymbol;
  }

  get business() {
    const { business = null } = this.$store.getters['business/getBusinessInfo'];

    return business || {};
  }

  get isEmptyData() {
    return Object.values(this.totalValues).every(
      ({ amount }) => Number(amount) === 0
    );
  }

  get queryParameters() {
    return {
      from: this.period.start,
      to: this.period.end,
      slug: this.periodActive
    };
  }

  mounted() {
    this.handleTriggerObserver();
  }

  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  }

  resetLoadedFlags(data: Record<string, any>): Record<string, any> {
    const updatedData: Record<string, any> = {};

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        updatedData[key] = {
          ...data[key],
          loaded: false
        };
      }
    }

    return updatedData;
  }

  changedPeriod(slug: EPeriodPerformance) {
    this.$emit('changedPeriod', slug);
    this.totalValues = this.resetLoadedFlags(this.totalValues);
  }

  handleTriggerObserver() {
    if (!this.isMobileDevice) return;

    const target = document.querySelector(
      '.performance-highlights__grid-trigger'
    );

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.triggerIntersected = entry.isIntersecting;
      },
      {
        root: document.querySelector('.performance-highlights__grid'),
        threshold: 0
      }
    );

    this.observer.observe(target as HTMLElement);
  }

  handleGoTo(route: string) {
    if (!route.includes('#')) return;

    const anchorHash = route.substring(route.indexOf('#'));

    goToElement(anchorHash);
  }

  getGrowth({ current, past }: { current: number; past: number }) {
    if (this.periodActive === EPeriodPerformance.OVERTIME || current === past)
      return {
        insightVariant: '',
        arrowDirection: '',
        insightText: ''
      };

    if (!past) {
      return {
        insightVariant: 'normal',
        arrowDirection: '',
        insightText: `No data ${PerformanceUtils.getComparativeLabelV2(
          this.periodActive
        )}`
      };
    }

    let growthPercentage = (((current - past) / past) * 100).toFixed(0);

    if (current > past) {
      return {
        insightVariant: 'success',
        arrowDirection: EAnalyticsCardArrowDirection.UP,
        insightText: `${growthPercentage}% ${PerformanceUtils.getComparativeLabelV2(
          this.periodActive
        )}`
      };
    } else if (current < past) {
      return {
        insightVariant: 'danger',
        arrowDirection: EAnalyticsCardArrowDirection.DOWN,
        insightText: `${growthPercentage}% ${PerformanceUtils.getComparativeLabelV2(
          this.periodActive
        )}`
      };
    }
  }

  @Watch('performanceData', { deep: true })
  async getOverview() {
    if (!this.performanceData) return;

    if (
      this.hasFavoritesMetric &&
      this.totalValues &&
      this.totalValues.totalFavorites
    ) {
      this.totalValues.totalFavorites.hideMetric = false;
    }

    const dataToUpdate = Object.keys(this.totalValues);

    const entries = Object.entries(Object.values(this.performanceData).reduce(
      (prev: any, current: any) => ({
        ...prev,
        ...current
      }),
      {}
    ) as any);

    let newTotalValues = this.totalValues;

    for (const entry of entries) {
      const [key, value]: any = entry;

      if (!dataToUpdate.includes(key)) continue;

      const totalValue = newTotalValues[key];

      Object.assign(totalValue, {
        loaded: true,
        amount: value.current,
        disabled: Number(value.current) === 0,
        ...(!totalValue.hideGrowth
          ? this.getGrowth({
              current: Number(value.current),
              past: Number(value.past)
            })
          : {})
      });

      if (
        !totalValue.hideMetric &&
        totalValue.trackEvent &&
        !this.eventsAlreadySent
      ) {
        // The event name and its property key: never in the merchant's
        // language (TWISM-20247).
        const metricLabel = String(
          this.$t(`${this.tPrefix}.cards.${key}.label`, 'en-US')
        );

        segmentIo.track(`Home | Viewed ${metricLabel} Metric`, {
          [`Number of ${metricLabel}`]: totalValue.amount
        });
      }
    }

    this.totalValues = { ...newTotalValues };

    this.loaded = true;
    this.eventsAlreadySent = true;
  }

  created() {
    this.getOverview();
  }
}
